/**
 * Telegram Mini App store
 * Manages Telegram WebApp SDK integration and user data
 */

import { writable, derived } from 'svelte/store';
import WebApp from '@twa-dev/sdk';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export interface TelegramWebAppData {
  initData: string;
  initDataUnsafe: any;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
}

export interface TelegramState {
  isInitialized: boolean;
  isAvailable: boolean;
  user: TelegramUser | null;
  webAppData: TelegramWebAppData | null;
  error: string | null;
}

/**
 * Create Telegram store
 */
function createTelegramStore() {
  const { subscribe, set, update } = writable<TelegramState>({
    isInitialized: false,
    isAvailable: false,
    user: null,
    webAppData: null,
    error: null,
  });

  return {
    subscribe,

    /**
     * Initialize Telegram WebApp SDK
     * Should be called when app detects Telegram environment
     */
    initialize: () => {
      try {
        // Check if Telegram WebApp is available
        if (typeof window === 'undefined' || !(window as any).Telegram?.WebApp) {
          update(state => ({
            ...state,
            isInitialized: true,
            isAvailable: false,
            error: 'Telegram WebApp not available',
          }));
          return;
        }

        // Initialize WebApp SDK
        WebApp.ready();

        // Expand the app to full height
        WebApp.expand();

        // Enable closing confirmation (optional)
        WebApp.enableClosingConfirmation();

        // Get user data
        const user = WebApp.initDataUnsafe?.user || null;

        // Get WebApp data
        const webAppData: TelegramWebAppData = {
          initData: WebApp.initData,
          initDataUnsafe: WebApp.initDataUnsafe,
          version: WebApp.version,
          platform: WebApp.platform,
          colorScheme: WebApp.colorScheme,
          themeParams: WebApp.themeParams,
          isExpanded: WebApp.isExpanded,
          viewportHeight: WebApp.viewportHeight,
          viewportStableHeight: WebApp.viewportStableHeight,
          headerColor: WebApp.headerColor,
          backgroundColor: WebApp.backgroundColor,
          isClosingConfirmationEnabled: WebApp.isClosingConfirmationEnabled,
        };

        // Update store
        set({
          isInitialized: true,
          isAvailable: true,
          user,
          webAppData,
          error: null,
        });

        // Log initialization success
        console.log('Telegram WebApp initialized:', {
          user,
          platform: WebApp.platform,
          version: WebApp.version,
          colorScheme: WebApp.colorScheme,
        });

        // Setup theme change listener
        WebApp.onEvent('themeChanged', () => {
          update(state => {
            if (!state.webAppData) return state;
            return {
              ...state,
              webAppData: {
                ...state.webAppData,
                colorScheme: WebApp.colorScheme,
                themeParams: WebApp.themeParams,
                headerColor: WebApp.headerColor,
                backgroundColor: WebApp.backgroundColor,
              },
            };
          });
        });

        // Setup viewport change listener
        WebApp.onEvent('viewportChanged', () => {
          update(state => {
            if (!state.webAppData) return state;
            return {
              ...state,
              webAppData: {
                ...state.webAppData,
                isExpanded: WebApp.isExpanded,
                viewportHeight: WebApp.viewportHeight,
                viewportStableHeight: WebApp.viewportStableHeight,
              },
            };
          });
        });

      } catch (error) {
        console.error('Failed to initialize Telegram WebApp:', error);
        update(state => ({
          ...state,
          isInitialized: true,
          isAvailable: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }));
      }
    },

    /**
     * Show Telegram back button
     */
    showBackButton: (onClick?: () => void) => {
      if (WebApp.BackButton) {
        WebApp.BackButton.show();
        if (onClick) {
          WebApp.BackButton.onClick(onClick);
        }
      }
    },

    /**
     * Hide Telegram back button
     */
    hideBackButton: () => {
      if (WebApp.BackButton) {
        WebApp.BackButton.hide();
      }
    },

    /**
     * Show Telegram main button
     */
    showMainButton: (text: string, onClick: () => void) => {
      if (WebApp.MainButton) {
        WebApp.MainButton.setText(text);
        WebApp.MainButton.onClick(onClick);
        WebApp.MainButton.show();
      }
    },

    /**
     * Hide Telegram main button
     */
    hideMainButton: () => {
      if (WebApp.MainButton) {
        WebApp.MainButton.hide();
      }
    },

    /**
     * Show Telegram popup
     */
    showPopup: (params: {
      title?: string;
      message: string;
      buttons?: Array<{ id?: string; type?: 'default' | 'destructive'; text: string }>;
    }) => {
      return new Promise<string>((resolve) => {
        if (WebApp.showPopup) {
          // Build popup params according to SDK requirements
          const popupParams: any = {
            message: params.message,
          };
          
          if (params.title) {
            popupParams.title = params.title;
          }
          
          if (params.buttons) {
            popupParams.buttons = params.buttons;
          }
          
          WebApp.showPopup(popupParams, (buttonId) => {
            resolve(buttonId || '');
          });
        } else {
          resolve('');
        }
      });
    },

    /**
     * Show Telegram alert
     */
    showAlert: (message: string) => {
      return new Promise<void>((resolve) => {
        if (WebApp.showAlert) {
          WebApp.showAlert(message, () => {
            resolve();
          });
        } else {
          alert(message);
          resolve();
        }
      });
    },

    /**
     * Show Telegram confirm
     */
    showConfirm: (message: string) => {
      return new Promise<boolean>((resolve) => {
        if (WebApp.showConfirm) {
          WebApp.showConfirm(message, (confirmed) => {
            resolve(confirmed);
          });
        } else {
          resolve(confirm(message));
        }
      });
    },

    /**
     * Send data to Telegram bot
     */
    sendData: (data: string) => {
      if (WebApp.sendData) {
        WebApp.sendData(data);
      }
    },

    /**
     * Close Telegram WebApp
     */
    close: () => {
      if (WebApp.close) {
        WebApp.close();
      }
    },

    /**
     * Open Telegram link
     */
    openLink: (url: string) => {
      if (WebApp.openLink) {
        WebApp.openLink(url);
      } else {
        window.open(url, '_blank');
      }
    },

    /**
     * Open Telegram link in internal browser
     */
    openTelegramLink: (url: string) => {
      if (WebApp.openTelegramLink) {
        WebApp.openTelegramLink(url);
      } else {
        window.location.href = url;
      }
    },

    /**
     * Trigger haptic feedback
     */
    hapticFeedback: (type: 'impact' | 'notification' | 'selection', style?: 'light' | 'medium' | 'heavy' | 'error' | 'success' | 'warning') => {
      if (WebApp.HapticFeedback) {
        if (type === 'impact' && style) {
          WebApp.HapticFeedback.impactOccurred(style as any);
        } else if (type === 'notification' && style) {
          WebApp.HapticFeedback.notificationOccurred(style as any);
        } else if (type === 'selection') {
          WebApp.HapticFeedback.selectionChanged();
        }
      }
    },
  };
}

export const telegram = createTelegramStore();

/**
 * Derived store for checking if Telegram is available and ready
 */
export const isTelegramReady = derived(
  telegram,
  ($telegram) => $telegram.isInitialized && $telegram.isAvailable
);

/**
 * Derived store for getting Telegram user
 */
export const telegramUser = derived(
  telegram,
  ($telegram) => $telegram.user
);

/**
 * Derived store for getting Telegram theme
 */
export const telegramTheme = derived(
  telegram,
  ($telegram) => $telegram.webAppData?.colorScheme || 'dark'
);

/**
 * Derived store for getting Telegram color scheme
 */
export const telegramThemeParams = derived(
  telegram,
  ($telegram) => $telegram.webAppData?.themeParams || {}
);
